import { ImpactResult, FlowAskResponse, FlowReportResponse, FlowExportResponse } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? '/api';

// demoSessionToken: when set, every request carries X-Demo-Session instead of a real
// Authorization bearer token — the server's tenantMiddleware treats these as two
// separate, parallel auth paths (see server/src/middleware/tenant.ts). getToken is never
// called in this mode, so a demo visitor never triggers an MSAL popup.
export function createFlowClient(getToken: () => Promise<string>, demoSessionToken?: string) {
  async function send(endpoint: string, body: object, token: string): Promise<Response> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (demoSessionToken) headers['X-Demo-Session'] = demoSessionToken;
    else headers['Authorization'] = `Bearer ${token}`;
    return fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
  }

  async function post<T>(endpoint: string, body: object): Promise<T> {
    let token = demoSessionToken ? '' : await getToken();
    let response = await send(endpoint, body, token);

    // A 401 here almost always means the token expired between acquisition and the
    // server validating it — re-acquire (MSAL refreshes silently if the session is
    // still valid) and retry once before surfacing an error to the user. Demo sessions
    // don't refresh — a 401 there means the session expired or was rejected, and the
    // caller (App.tsx) sends the visitor back to a fresh "Try a Sample Schedule".
    if (response.status === 401 && !demoSessionToken) {
      token = await getToken();
      response = await send(endpoint, body, token);
    }

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      let msg = text;
      let parsedBody: Record<string, unknown> | undefined;
      try { parsedBody = JSON.parse(text); msg = (parsedBody?.error as string) ?? text; } catch { /* ignore */ }
      const err = new Error(`Request failed (${response.status}): ${msg || response.statusText}`) as Error & { status?: number; body?: Record<string, unknown> };
      err.status = response.status;
      err.body = parsedBody;
      throw err;
    }

    const text = await response.text();
    if (!text) return {} as T;
    try { return JSON.parse(text) as T; } catch { return { result: text } as unknown as T; }
  }

  return {
    projects: (userEmail: string) =>
      post<{ projects: any[]; user: { name: string; role: string; company: string } }>('/projects', { user_email: userEmail }),

    upload: (userEmail: string, projectId: string, fileName: string, fileContent: string) =>
      post<{ success: boolean; activity_count: number; message: string }>('/upload', {
        user_email: userEmail,
        project_id: projectId,
        file_name: fileName,
        file_content: fileContent,
      }),

    impact: (
      userEmail: string,
      projectId: string,
      activityId: string,
      fieldName: string,
      oldValue: string | number,
      newValue: string | number
    ) =>
      post<ImpactResult>('/impact', {
        user_email: userEmail,
        project_id: projectId,
        activity_id: activityId,
        field_name: fieldName,
        old_value: oldValue,
        new_value: newValue,
      }),

    approve: (
      userEmail: string,
      projectId: string,
      activityId: string,
      fieldName: string,
      newValue: string | number,
      oldValue: string | number,
      impactRiskLevel: string,
      impactSummary: string
    ) =>
      post<void>('/approve', {
        user_email: userEmail,
        project_id: projectId,
        activity_id: activityId,
        field_name: fieldName,
        new_value: newValue,
        old_value: oldValue,
        impact_risk_level: impactRiskLevel,
        impact_summary: impactSummary,
        localDate: new Date().toLocaleDateString('en-CA'), // YYYY-MM-DD in the user's local timezone
      }),

    approveBatch: (
      userEmail: string,
      projectId: string,
      activityId: string,
      changes: { field_name: string; new_value: string | number; old_value: string | number }[],
      impactRiskLevel: string,
      impactSummary: string
    ) =>
      post<{ success: boolean; updatedActivity: Record<string, any> | null }>('/approve-batch', {
        user_email: userEmail,
        project_id: projectId,
        activity_id: activityId,
        changes,
        impact_risk_level: impactRiskLevel,
        impact_summary: impactSummary,
        localDate: new Date().toLocaleDateString('en-CA'), // YYYY-MM-DD in the user's local timezone
      }),

    approveMultiBatch: (
      userEmail: string,
      projectId: string,
      activities: { activity_id: string; changes: { field_name: string; new_value: string | number; old_value: string | number }[] }[],
      impactRiskLevel: string,
      impactSummary: string
    ) =>
      post<{
        success: boolean;
        activitiesUpdated: number;
        updatedActivities: Record<string, any>[];
        completion_date: string | null;
        completion_date_before: string | null;
        completion_date_changed: boolean;
        completion_date_days_diff: number;
        newly_critical: { activity_id: string; activity_name: string }[];
        critical_path: { activity_id: string; activity_name: string; is_new: boolean }[];
      }>('/approve-batch', {
        user_email: userEmail,
        project_id: projectId,
        activities,
        impact_risk_level: impactRiskLevel,
        impact_summary: impactSummary,
        localDate: new Date().toLocaleDateString('en-CA'), // YYYY-MM-DD in the user's local timezone
      }),

    ask: (userEmail: string, projectId: string, questionText: string) =>
      post<FlowAskResponse>('/ask', {
        user_email: userEmail,
        project_id: projectId,
        question_text: questionText,
      }),

    report: (userEmail: string, projectId: string, reportPeriod: string, audienceTone: string) =>
      post<FlowReportResponse>('/report', {
        user_email: userEmail,
        project_id: projectId,
        report_period: reportPeriod,
        audience_tone: audienceTone,
      }),

    exportP6: (userEmail: string, projectId: string) =>
      post<FlowExportResponse>('/export', {
        user_email: userEmail,
        project_id: projectId,
      }),

    exportXer: (userEmail: string, projectId: string) =>
      post<FlowExportResponse>('/export/xer', {
        user_email: userEmail,
        project_id: projectId,
      }),

    exportXml: (userEmail: string, projectId: string) =>
      post<FlowExportResponse>('/export/xml', {
        user_email: userEmail,
        project_id: projectId,
      }),

    // Admin Center — the server derives the environment from its own deployment
    // config; none of these calls can select one.
    adminEnvironment: () =>
      post<{ environment: AdminEnvironment; mainSiteUrl: string }>('/admin/environment', {}),

    adminListProjects: (email: string) =>
      post<{ projects: { project_id: string; project_name: string }[]; siteUrl: string; registered: boolean }>('/admin/projects', { email }),

    adminGenerateProjectId: (email: string) =>
      post<{ project_id: string; siteUrl: string }>('/admin/generate-project-id', { email }),

    adminCheckSite: (email: string, siteUrl: string) =>
      post<{ environment: AdminEnvironment; siteUrl: string; state: 'empty' | 'partial' | 'complete'; totalLists: number; listsToCreate: string[]; listsPresent: string[] }>(
        '/admin/check-site', { email, site_url: siteUrl }
      ),

    approveAndInvite: (params: {
      email: string;
      name: string;
      role: 'PM' | 'Viewer';
      project_id?: string;
      project_name?: string;
      generate_project_id?: boolean;
      gc_company?: string;
      new_company_site_url?: string;
      confirmed_production?: boolean;
      confirmed_provision?: boolean;
    }) =>
      post<{ job_id: string; environment: AdminEnvironment }>('/admin/approve-and-invite', params),

    adminJob: (jobId: string) =>
      post<AdminJobState>('/admin/job', { job_id: jobId }),

    adminInvites: () =>
      post<{ environment: AdminEnvironment; invites: AdminInviteRow[] }>('/admin/invites', {}),

    adminRefreshInvites: () =>
      post<{ environment: AdminEnvironment; checked: number; updated: number; permissionMissing: boolean; note: string | null; errors: string[]; invites: AdminInviteRow[] }>(
        '/admin/invites/refresh', {}
      ),
  };
}

export type FlowClient = ReturnType<typeof createFlowClient>;

export type AdminEnvironment = 'staging' | 'production';

export type AdminStepStatus = 'pending' | 'running' | 'done' | 'failed' | 'skipped' | 'not_run';
export interface AdminStep { key: string; label: string; status: AdminStepStatus; detail?: string }

export interface AdminInviteResult {
  ok: boolean;
  environment: AdminEnvironment;
  mode: 'existing' | 'new-company';
  targetSiteUrl: string | null;
  email: string;
  name: string;
  company: string;
  projectId: string | null;
  projectName: string | null;
  projectCreated: boolean;
  role: 'PM' | 'Viewer' | null;
  inviteCreated: boolean;
  inviteRedeemUrl: string | null;
  emailSent: boolean;
  emailError: string | null;
  inviteRecord: 'created' | 'updated' | null;
  steps: AdminStep[];
}

export interface AdminJobState {
  status: 'running' | 'done' | 'error';
  steps: AdminStep[];
  log: string[];
  result: AdminInviteResult | null;
  error: string | null;
  environment: AdminEnvironment;
}

export interface AdminInviteRow {
  id: string;
  email: string;
  status: 'Sent' | 'Pending acceptance' | 'Accepted' | 'Failed';
  company: string;
  company_domain: string;
  project_id: string;
  project_name: string;
  role: string;
  sent_date: string | null;
  site_url: string;
  invited_by: string;
  status_detail: string;
  status_checked_at: string | null;
}
